import {useMemo, useState} from 'react'
import {Modal, Box} from '@mui/material'
import {KTSVG} from '../../../../_metronic/helpers'
import {assignChannelCategoryRequest} from '../../../../api/endpoints/channelCategory'
import {SHOW_ERROR, SHOW_SUCCESS} from '../../../../util'
import {getCategoryChannels} from '../utils'

const style = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 700,
  maxWidth: '95vw',
  bgcolor: 'white',
  boxShadow: 24,
  p: 4,
  height: 700,
  maxHeight: '90vh',
  display: 'flex',
  flexDirection: 'column' as 'column',
}

interface Props {
  category: any
  categories: any[]
  channels: any[]
  onClose: () => void
  onAssigned: () => Promise<void>
}

const AssignChannelsModal = ({category, categories, channels, onClose, onAssigned}: Props) => {
  const [search, setSearch] = useState('')
  // channelId -> desired state (true = assign, false = unassign); only holds unsaved changes
  const [changes, setChanges] = useState<Map<number, boolean>>(new Map())
  const [saving, setSaving] = useState(false)

  // channelId -> category it currently belongs to
  const channelCategoryMap = useMemo(() => {
    const map = new Map<number, any>()
    categories.forEach((cat) => {
      getCategoryChannels(cat).forEach((ch: any) => map.set(Number(ch?.id), cat))
    })
    return map
  }, [categories])

  const getChannelCategory = (channel: any) => {
    const fromList = channelCategoryMap.get(Number(channel?.id))
    if (fromList) return fromList
    const id = channel?.channelCategoryId ?? channel?.channelCategory?.id
    return id ? categories.find((c) => c.id === id) ?? {id} : null
  }

  const filteredChannels = useMemo(
    () =>
      channels.filter(
        (ch) => ch?.isActive && ch?.name?.toLowerCase().includes(search.trim().toLowerCase())
      ),
    [channels, search]
  )

  const isInThisCategory = (channel: any) => getChannelCategory(channel)?.id === category.id

  const isChecked = (channel: any) =>
    changes.has(channel.id) ? (changes.get(channel.id) as boolean) : isInThisCategory(channel)

  const toggle = (channel: any) => {
    const next = !isChecked(channel)
    setChanges((prev) => {
      const updated = new Map(prev)
      // Back to its saved state -> nothing to send for this channel
      if (next === isInThisCategory(channel)) updated.delete(channel.id)
      else updated.set(channel.id, next)
      return updated
    })
  }

  const toAssign = Array.from(changes.entries()).filter(([, v]) => v).map(([id]) => id)
  const toUnassign = Array.from(changes.entries()).filter(([, v]) => !v).map(([id]) => id)
  const movingFromOther = toAssign.some((id) =>
    getChannelCategory(channels.find((ch) => ch.id === id))
  )

  const handleSave = async () => {
    if (!changes.size) return
    setSaving(true)
    const results = await Promise.allSettled(
      Array.from(changes.entries()).map(([channelId, assign]) =>
        assignChannelCategoryRequest({channelId, channelCategoryId: assign ? category.id : null})
      )
    )
    const failed = results.filter((r) => r.status === 'rejected') as PromiseRejectedResult[]

    if (failed.length < results.length) {
      SHOW_SUCCESS(true, `${category.name} updated`)
    }
    if (failed.length) {
      SHOW_ERROR(
        true,
        failed[0]?.reason?.response?.data?.message ?? `${failed.length} channel(s) failed to update`
      )
    }

    await onAssigned()

    // Close on full success; keep open on failure so the admin can retry
    if (!failed.length) {
      onClose()
      return
    }
    setChanges(new Map())
    setSaving(false)
  }

  const handleClose = () => {
    if (!saving) onClose()
  }

  return (
    <Modal open onClose={handleClose}>
      <Box sx={style}>
        <div className='modal-header mb-2'>
          <h2 className='fw-bolder mb-0'>Assign Channels — {category?.name}</h2>
          <div
            onClick={handleClose}
            className='btn btn-icon btn-sm btn-active-icon-primary'
            style={{cursor: 'pointer'}}
          >
            <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-1' />
          </div>
        </div>

        <div className='d-flex align-items-center position-relative my-3'>
          <KTSVG
            path='/media/icons/duotune/general/gen021.svg'
            className='svg-icon-1 position-absolute ms-4'
          />
          <input
            type='text'
            className='form-control form-control-solid ps-14'
            placeholder='Search channels'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className='flex-grow-1 overflow-auto'>
          {filteredChannels.length === 0 && (
            <div className='text-muted text-center py-10'>No active channels found</div>
          )}
          {filteredChannels.map((channel) => {
            const current = getChannelCategory(channel)
            const inThisCategory = current?.id === category.id
            const pending = changes.get(channel.id)
            return (
              <label
                key={channel.id}
                className='d-flex align-items-center justify-content-between border-bottom py-3 px-2'
                style={{cursor: 'pointer'}}
              >
                <div className='d-flex align-items-center'>
                  <input
                    type='checkbox'
                    className='form-check-input me-4'
                    checked={isChecked(channel)}
                    disabled={saving}
                    onChange={() => toggle(channel)}
                  />
                  {channel?.icon && (
                    <img
                      src={channel.icon}
                      alt='icon'
                      style={{width: 26, height: 26, marginRight: 10}}
                    />
                  )}
                  <span className='fw-semibold'>{channel?.name}</span>
                </div>
                {pending === false ? (
                  <span className='badge badge-light-danger'>Will be unassigned</span>
                ) : inThisCategory ? (
                  <span className='badge badge-light-success'>Assigned</span>
                ) : current ? (
                  <span className='badge badge-light-warning'>
                    In: {current?.name ?? `#${current.id}`}
                  </span>
                ) : null}
              </label>
            )
          })}
        </div>

        <div className='d-flex justify-content-between align-items-center pt-4'>
          <span className='text-muted'>
            {toAssign.length} to assign · {toUnassign.length} to unassign
            {movingFromOther && ' · channels already in another category will be moved'}
          </span>
          <button
            type='button'
            className='btn btn-primary'
            disabled={!changes.size || saving}
            onClick={handleSave}
          >
            {saving ? (
              <>
                Saving...
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </>
            ) : (
              `Save${changes.size ? ` (${changes.size})` : ''}`
            )}
          </button>
        </div>
      </Box>
    </Modal>
  )
}

export default AssignChannelsModal
